import { EyeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { MultiSelect } from '../multi-select';
import { ProviderImage } from '../provider-image';
import { SkeletonLoader } from '../skeleton-loader';
import { Tooltip } from '../tooltip';
import {
  DetectionFilter,
  FilterControls,
  IntegrationFilter,
} from './filter-types';

export const DetectionFilterFC = ({
  error,
  data,
  id,
  label,
  loading,
  value,
  onClose,
  filters,
  setValue,
  filteredValues,
  placement,
  displayMaxItems,
  selectText,
  permanent,
  showInlineClear,
  hideIcon,
  showOperator,
  wrapperClassName,
  dataTestId,
}: DetectionFilter & FilterControls) => {
  const provider = filters.find((filter) => filter.type === 'provider')?.id;
  const providerValues =
    provider && filteredValues
      ? (filteredValues[provider] as string[]) || []
      : [];
  const integration = filters.find(
    (filter) => filter.type === 'integrationId',
  ) as IntegrationFilter | undefined;
  const integrationProviderValues =
    integration && filteredValues
      ? ((filteredValues[integration.id] as string[]) || []).map((x) => {
          return integration.data?.find((int) => int.integrationId === x)
            ?.provider;
        })
      : [];

  return (
    <SkeletonLoader
      status={loading ? 'loading' : error ? 'error' : 'success'}
      error={error}
      type="line"
      key={id}
    >
      <MultiSelect
        dataTestId={dataTestId}
        label={label}
        displayButtonClassName={wrapperClassName}
        showOperator={showOperator}
        options={
          data
            ?.filter((det) => {
              return (
                (!providerValues.length && !integrationProviderValues.length) ||
                providerValues?.includes(det.provider || '') ||
                !integrationProviderValues ||
                integrationProviderValues?.includes(det.provider || '') ||
                value.includes(det.id || '')
              );
            })
            .map((det) => ({
              value: det.id || '',
              searchString: `${det.title} ${det.provider} ${det.id} ${det.slug ?? ''}`,
              label: (
                <div className="flex flex-col truncate">
                  <div className="flex items-center gap-1">
                    <ProviderImage
                      sizeClassName="w-8 h-6 flex-shrink-0"
                      provider={det.provider}
                    />
                    <div className="truncate">{det.title}</div>
                    <Tooltip
                      label={det.title || ''}
                      style={{
                        maxWidth: '500px',
                      }}
                    >
                      <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-400" />
                    </Tooltip>
                  </div>
                  <span className="inline-flex text-xs text-gray-600 dark:text-gray-400">
                    {det.isCustom && det.slug ? det.slug : det.id}
                  </span>
                </div>
              ),
              selectedDisplay: (
                <div className="flex items-center gap-1">
                  <ProviderImage
                    sizeClassName="w-8 h-6 flex-shrink-0"
                    provider={det.provider}
                  />
                  <div className="truncate">
                    {det.isCustom && det.slug ? det.slug : det.id}
                  </div>
                </div>
              ),
            })) || []
        }
        mode="multiple"
        icon={EyeIcon}
        allowSearch
        virtualize
        showSelectAll
        value={value || []}
        setValue={setValue}
        placement={placement}
        onClose={onClose}
        className={`h-[40px]`}
        displayMaxItems={displayMaxItems}
        selectText={selectText}
        permanent={permanent}
        showInlineClear={showInlineClear}
        hideIcon={hideIcon}
      />
    </SkeletonLoader>
  );
};
